import { useState, useEffect, useCallback } from 'react';
import { Button, Input } from 'semantic-ui-react';
import { debounce } from 'lodash';
import { SCALE_CONSTANTS } from '../pdfViewer/constants';
import './scaleControl.css';

type ScaleControlPropsType = {
    scale: number;
    onScaleChange: (scale: number) => void;
    className?: string;
};

const findNextPredefinedScale = (currentScale: number, increase: boolean): number => {
    const scales = SCALE_CONSTANTS.PREDEFINED_SCALES;
    if (increase) {
        return scales.find((scale) => scale > currentScale) ?? SCALE_CONSTANTS.MAX_SCALE;
    }
    return [...scales].reverse().find((scale) => scale < currentScale) ?? SCALE_CONSTANTS.MIN_SCALE;
};

const clampScale = (scale: number): number => {
    if (scale < SCALE_CONSTANTS.MIN_SCALE) {
        return SCALE_CONSTANTS.MIN_SCALE;
    }
    if (scale > SCALE_CONSTANTS.MAX_SCALE) {
        return SCALE_CONSTANTS.MAX_SCALE;
    }
    return scale;
};

const ScaleControl = ({ scale, onScaleChange, className = '' }: ScaleControlPropsType) => {
    const [inputValue, setInputValue] = useState(Math.round(scale * 100));

    useEffect(() => {
        setInputValue(Math.round(scale * 100));
    }, [scale]);

    const updateScale = useCallback(debounce(onScaleChange, 500), [onScaleChange]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(e.target.value);
        setInputValue(newValue);
        if (Number.isNaN(newValue)) {
            return;
        }
        updateScale(clampScale(newValue / 100));
    };

    return (
        <div className={`scale-control ${className}`}>
            <Button
                icon="minus"
                onClick={() => onScaleChange(findNextPredefinedScale(scale, false))}
                disabled={scale <= SCALE_CONSTANTS.MIN_SCALE}
            />
            <Input
                label="%"
                labelPosition="right"
                type="text"
                value={inputValue}
                onChange={handleInputChange}
            />
            <Button
                icon="plus"
                onClick={() => onScaleChange(findNextPredefinedScale(scale, true))}
                disabled={scale >= SCALE_CONSTANTS.MAX_SCALE}
            />
        </div>
    );
};

export default ScaleControl;
